import { useState, useEffect } from 'react';
import { ArrowPathIcon, ClockIcon, XMarkIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_ENDPOINTS } from '../../config/environment';

// Generic field types for the editor
export interface FieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'select' | 'boolean' | 'json' | 'array' | 'object' | 'environmental' | 'labor' | 'production_locations';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  description?: string;
  validation?: (value: any) => string | null;
  nested?: FieldDefinition[];
  min?: number;
  max?: number;
  step?: number;
}

export interface EntityVersion {
  id: string;
  version_number: number;
  change_type: string;
  change_summary?: string;
  changed_fields?: string[];
  created_by?: string;
  created_at: string;
  entity_data: any;
}

export interface GenericEntityEditorProps {
  entityType: string;
  entityId: string;
  entityData: any;
  fieldDefinitions: FieldDefinition[];
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  title: string;
  apiEndpoint: string;
  showVersions?: boolean;
  showEnvironmental?: boolean;
  showSubEntities?: boolean;
  subEntityType?: string;
}

const GenericEntityEditor: React.FC<GenericEntityEditorProps> = ({
  entityType,
  entityId,
  entityData,
  fieldDefinitions,
  onSave,
  onCancel,
  title,
  showVersions = true,
  showEnvironmental = true,
  showSubEntities = false,
  subEntityType
}) => {
  const [editedData, setEditedData] = useState<any>(entityData);
  const [versions, setVersions] = useState<EntityVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<EntityVersion | null>(null);
  const [activeTab, setActiveTab] = useState<'edit' | 'versions' | 'environmental' | 'sub_entities'>('edit');
  const [isSaving, setIsSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Load version history
  const loadVersions = async () => {
    if (!showVersions) return;
    
    try {
      setIsLoadingVersions(true);
      const response = await fetch(API_ENDPOINTS.admin.versions(entityType, entityId));
      if (response.ok) {
        const versionsData = await response.json();
        setVersions(versionsData);
      }
    } catch (error) {
      console.error('Failed to load versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setIsLoadingVersions(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [entityId, entityType]);

  // Handle field value changes
  const handleFieldChange = (fieldKey: string, value: any, parentKey?: string) => {
    setEditedData((prev: any) => {
      const newData = { ...prev };
      
      if (parentKey) {
        // Handle nested object fields
        if (!newData[parentKey]) {
          newData[parentKey] = {};
        }
        newData[parentKey] = { ...newData[parentKey], [fieldKey]: value };
      } else {
        newData[fieldKey] = value;
      }
      
      return newData;
    });

    // Clear validation error for this field
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Validate all fields
  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};
    
    fieldDefinitions.forEach(field => {
      const value = editedData[field.key];
      
      // Required field validation
      if (field.required && (value === undefined || value === null || value === '')) {
        errors[field.key] = `${field.label} is required`;
      }
      
      // Custom validation
      if (field.validation && value !== undefined && value !== null && value !== '') {
        const validationError = field.validation(value);
        if (validationError) {
          errors[field.key] = validationError;
        }
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateFields()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    try {
      setIsSaving(true);
      await onSave(editedData);
      toast.success(`${title} updated successfully`);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(`Failed to update ${title.toLowerCase()}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle version revert
  const handleRevertToVersion = async (version: EntityVersion) => {
    if (!confirm(`Revert to version ${version.version_number}? This will overwrite current data.`)) {
      return;
    }

    try {
      // Create a revert action
      const revertEndpoint = entityType === 'product' 
        ? API_ENDPOINTS.admin.revertProduct(entityId)
        : API_ENDPOINTS.admin.revertComponent(entityId);
      const response = await fetch(revertEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          version_id: version.id,
          revert_reason: `Reverted to version ${version.version_number} via admin interface`,
          created_by: 'admin_user'
        }),
      });

      if (response.ok) {
        setEditedData(version.entity_data);
        toast.success(`Reverted to version ${version.version_number}`);
        await loadVersions(); // Reload versions
        // Trigger a save to update the parent component
        await onSave(version.entity_data);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to revert');
      }
    } catch (error) {
      console.error('Failed to revert:', error);
      toast.error('Failed to revert to selected version');
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  // Render field based on type
  const renderField = (field: FieldDefinition, parentKey?: string) => {
    const fieldKey = parentKey ? `${parentKey}.${field.key}` : field.key;
    const value = parentKey ? editedData[parentKey]?.[field.key] : editedData[field.key];
    const error = validationErrors[fieldKey];

    const baseClasses = `w-full border rounded-md px-3 py-2 text-sm ${
      error ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
    } focus:outline-none focus:ring-1 focus:ring-blue-500`;

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value, parentKey)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, parseFloat(e.target.value) || 0, parentKey)}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            step={field.step || 0.01}
            className={baseClasses}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value, parentKey)}
            placeholder={field.placeholder}
            rows={3}
            className={baseClasses}
          />
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value, parentKey)}
            className={baseClasses}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'boolean':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleFieldChange(field.key, e.target.checked, parentKey)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">{field.label}</span>
          </label>
        );

      case 'json':
        return (
          <textarea
            value={value ? JSON.stringify(value, null, 2) : ''}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleFieldChange(field.key, parsed, parentKey);
              } catch {
                // Invalid JSON, keep as string for now
                handleFieldChange(field.key, e.target.value, parentKey);
              }
            }}
            placeholder={field.placeholder || 'Enter valid JSON'}
            rows={4}
            className={baseClasses + ' font-mono text-xs'}
          />
        );

      case 'environmental':
        return (
          <div className="space-y-4 p-4 bg-green-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Carbon Footprint (kg CO₂)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={value?.carbon_footprint_kg || ''}
                  onChange={(e) => handleFieldChange('carbon_footprint_kg', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Energy Consumption (kWh)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={value?.energy_consumption_kwh || ''}
                  onChange={(e) => handleFieldChange('energy_consumption_kwh', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Water Usage (liters)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={value?.water_usage_liters || ''}
                  onChange={(e) => handleFieldChange('water_usage_liters', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Waste Generation (kg)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={value?.waste_generation_kg || ''}
                  onChange={(e) => handleFieldChange('waste_generation_kg', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recyclability (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={value?.recyclability_percentage || ''}
                  onChange={(e) => handleFieldChange('recyclability_percentage', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renewable Energy (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={value?.renewable_energy_percentage || ''}
                  onChange={(e) => handleFieldChange('renewable_energy_percentage', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
            </div>
          </div>
        );

      case 'labor':
        return (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Worker Count
                </label>
                <input
                  type="number"
                  min="0"
                  value={value?.worker_count || ''}
                  onChange={(e) => handleFieldChange('worker_count', parseInt(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Skill Level
                </label>
                <select
                  value={value?.skill_level || ''}
                  onChange={(e) => handleFieldChange('skill_level', e.target.value, field.key)}
                  className={baseClasses}
                >
                  <option value="">Select skill level</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Wage (USD/hour)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={value?.average_wage_usd_hour || ''}
                  onChange={(e) => handleFieldChange('average_wage_usd_hour', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Working Hours/Week
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="168"
                  value={value?.working_hours_per_week || ''}
                  onChange={(e) => handleFieldChange('working_hours_per_week', parseFloat(e.target.value) || null, field.key)}
                  className={baseClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Working Conditions
                </label>
                <select
                  value={value?.working_conditions || ''}
                  onChange={(e) => handleFieldChange('working_conditions', e.target.value, field.key)}
                  className={baseClasses}
                >
                  <option value="">Select conditions</option>
                  <option value="poor">Poor</option>
                  <option value="fair">Fair</option>
                  <option value="good">Good</option>
                  <option value="excellent">Excellent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Safety Rating
                </label>
                <select
                  value={value?.safety_rating || ''}
                  onChange={(e) => handleFieldChange('safety_rating', e.target.value, field.key)}
                  className={baseClasses}
                >
                  <option value="">Select rating</option>
                  <option value="poor">Poor</option>
                  <option value="fair">Fair</option>
                  <option value="good">Good</option>
                  <option value="excellent">Excellent</option>
                </select>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value, parentKey)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );
    }
  };

  // Group fields by section
  const groupedFields = fieldDefinitions.reduce((acc, field) => {
    const section = field.key.includes('environmental') ? 'environmental' :
                   field.key.includes('labor') ? 'labor' :
                   field.key.includes('production') ? 'production' : 'basic';
    
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {} as Record<string, FieldDefinition[]>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl mx-4 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'edit' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Edit Data
          </button>
          {showVersions && (
            <button
              onClick={() => setActiveTab('versions')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'versions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Version History ({versions.length})
            </button>
          )}
          {showEnvironmental && (
            <button
              onClick={() => setActiveTab('environmental')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'environmental' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Environmental Impact
            </button>
          )}
          {showSubEntities && subEntityType && (
            <button
              onClick={() => setActiveTab('sub_entities')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'sub_entities' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {subEntityType}s
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'edit' && (
            <div className="space-y-6">
              {Object.entries(groupedFields).map(([sectionKey, fields]) => (
                <div key={sectionKey} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                  >
                    <h4 className="text-lg font-medium text-gray-900 capitalize">
                      {sectionKey.replace('_', ' ')} Information
                    </h4>
                    {expandedSections.has(sectionKey) ? (
                      <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.has(sectionKey) && (
                    <div className="p-4 border-t border-gray-200 space-y-4">
                      {fields.map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.description && (
                            <p className="text-xs text-gray-500 mb-2">{field.description}</p>
                          )}
                          {renderField(field)}
                          {validationErrors[field.key] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[field.key]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">Version History</h4>
                <button
                  onClick={loadVersions}
                  disabled={isLoadingVersions}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`w-4 h-4 mr-2 ${isLoadingVersions ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {isLoadingVersions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  <span className="ml-3 text-gray-600">Loading versions...</span>
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-8">
                  <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No version history available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map(version => (
                    <div key={version.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-900">Version {version.version_number}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            version.change_type === 'create' ? 'bg-green-100 text-green-800' :
                            version.change_type === 'update' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {version.change_type}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setSelectedVersion(selectedVersion?.id === version.id ? null : version)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            {selectedVersion?.id === version.id ? 'Hide Details' : 'View Details'}
                          </button>
                          <button
                            onClick={() => handleRevertToVersion(version)}
                            className="px-3 py-1 text-sm text-orange-600 border border-orange-300 rounded hover:bg-orange-50"
                          >
                            Revert
                          </button>
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <p>{version.change_summary || 'No summary available'}</p>
                        <p className="mt-1">
                          {new Date(version.created_at).toLocaleString()}
                          {version.created_by && ` by ${version.created_by}`}
                        </p>
                        {version.changed_fields && version.changed_fields.length > 0 && (
                          <p className="mt-1">
                            Changed fields: {version.changed_fields.join(', ')}
                          </p>
                        )}
                      </div>

                      {selectedVersion?.id === version.id && (
                        <div className="mt-4 p-3 bg-gray-50 rounded">
                          <h5 className="font-medium text-gray-900 mb-2">Version Data:</h5>
                          <pre className="text-xs text-gray-700 overflow-x-auto">
                            {JSON.stringify(version.entity_data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'environmental' && (
            <div className="space-y-6">
              <h4 className="text-lg font-medium text-gray-900">Environmental Impact Data</h4>
              
              {/* Environmental impact visualization */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-green-600 text-xl mr-2">🌱</span>
                    <h5 className="font-medium text-gray-900">Carbon Footprint</h5>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {editedData.overall_environmental_impact?.carbon_footprint_kg || editedData.carbon_footprint_kg || 'N/A'}
                    {(editedData.overall_environmental_impact?.carbon_footprint_kg || editedData.carbon_footprint_kg) && ' kg CO₂'}
                  </p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-blue-600 text-xl mr-2">⚡</span>
                    <h5 className="font-medium text-gray-900">Energy Use</h5>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">
                    {editedData.overall_environmental_impact?.energy_consumption_kwh || editedData.energy_consumption_kwh || 'N/A'}
                    {(editedData.overall_environmental_impact?.energy_consumption_kwh || editedData.energy_consumption_kwh) && ' kWh'}
                  </p>
                </div>
                
                <div className="bg-cyan-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-cyan-600 text-xl mr-2">💧</span>
                    <h5 className="font-medium text-gray-900">Water Usage</h5>
                  </div>
                  <p className="text-2xl font-bold text-cyan-600">
                    {editedData.overall_environmental_impact?.water_usage_liters || editedData.water_usage_liters || 'N/A'}
                    {(editedData.overall_environmental_impact?.water_usage_liters || editedData.water_usage_liters) && ' L'}
                  </p>
                </div>
              </div>

              {/* Edit environmental data */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-4">Edit Environmental Data</h5>
                {renderField({
                  key: 'overall_environmental_impact',
                  label: 'Environmental Impact',
                  type: 'environmental'
                })}
              </div>
            </div>
          )}

          {activeTab === 'sub_entities' && subEntityType && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">{subEntityType}s</h4>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Add {subEntityType}
                </button>
              </div>
              
              <div className="text-center py-8">
                <p className="text-gray-600">Sub-entity management coming soon...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {isSaving ? (
              <>
                <div className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenericEntityEditor; 